/**
 * AGC: PVEBattle Component - Player vs Environment Battle
 * AGC: Uses P2PBattleManager contract with bot as opponent
 * AGC: Player pays 50 OGB + NFTs, bot auto-pays, winner gets 75 OGB
 */

import React, { useState, useEffect, useCallback, useRef } from 'react'

import { usePlayerProgression } from '../hooks/usePlayerProgression'

import { usePlayerHistory } from '../hooks/usePlayerHistory'

import { ethers } from 'ethers'

import { BuffSelector } from './BuffSelector' // AGC: Reusable buff selector component

import { BattleResultModal } from './modals/BattleResultModal'
import { useModal } from '../hooks/useModal'

import { InteractivePvEBattle } from './InteractivePvEBattle' // AGC: Import on-chain interactive battle component

import { HybridPvEBattle } from './HybridPvEBattle' // AGC: Import hybrid backend battle component

import { useWebSocket } from '../hooks/useWebSocket' // AGC: WebSocket for hybrid mode

import { buildBackendUrl } from '../utils/backendUrl'

import { ACTIVE_RPC_URL } from '../hooks/useContract'

import { useAccount } from 'wagmi'

import { useFeatureFlags } from '../hooks/useFeatureFlags'

import { getSafeSigner } from '../utils/getBrowserProvider'

// AGC: formatOgbBalanceDisplay and parseNumberSafe no longer needed - PlayerProgressionPanel handles formatting
import { unlockAudioPlayback } from '../utils/audio'
import { BottomNavKey } from './BottomNav'
import { usePlayerStatus } from '../hooks/useOGBToken' // AGC: Use same hook as TopNav for balance
import { DifficultySelector, Difficulty } from './DifficultySelector' // AGC: Reusable difficulty selector



interface ModeSelectionData {
  battleId: string
  difficulty: string
  selectedBuffs: number[]
  playerLevel: number
}

interface QuickBattleProps {
  playerAddress: string
  onBattleComplete?: (result: any) => void
  onNavigateToMarketplace?: () => void // AGC: Navigate to marketplace
  onNavClick: (nav: BottomNavKey) => void
  activeNav: BottomNavKey
  onOpenSuggestion: () => void
  onOpenSettings: () => void
  onOpenInventory: () => void
  onShowModeSelection?: (data: ModeSelectionData) => void // AGC: Callback to show mode selection modal
  pendingModeSelection?: { mode: 'onchain' | 'hybrid'; data: ModeSelectionData } | null // AGC: Pending mode selection coming from MainMenu
  onConsumeModeSelection?: () => void // AGC: Clear pending mode selection once processed
}



export const QuickBattle: React.FC<QuickBattleProps> = ({
  playerAddress,
  onBattleComplete,
  onNavigateToMarketplace, // AGC: Marketplace navigation
  onNavClick,
  activeNav: _activeNav,
  onOpenSuggestion: _onOpenSuggestion,
  onOpenSettings: _onOpenSettings,
  onOpenInventory: _onOpenInventory,
  onShowModeSelection,
  pendingModeSelection: _pendingModeSelection,
  onConsumeModeSelection: _onConsumeModeSelection,
}) => {

  const { address } = useAccount() // AGC: Get address for usePlayerStatus
  const { balance: ogbBalance } = usePlayerStatus(address) // AGC: Still needed for payment checks

  const { playerStats, calculateBattleXP } = usePlayerProgression(playerAddress)

  const { addBattleRecord } = usePlayerHistory(playerAddress)

  const { socket, isConnected } = useWebSocket() // AGC: WebSocket for hybrid mode

  const { connector } = useAccount()

  const { isFarcasterContext } = useFeatureFlags()
  
  // AGC: Modal hook for Base App compatible alerts
  const { alert, Modals } = useModal()

  const [statusBanner, setStatusBanner] = useState<{ type: 'error' | 'success'; message: string } | null>(null)

  const [difficulty, setDifficulty] = useState<Difficulty>('normal')

  const [selectedBuffs, setSelectedBuffs] = useState<number[]>([])
  const [selectedPotion, setSelectedPotion] = useState<number | null>(null)

  const [battleId, setBattleId] = useState<string>('')

  const [botAddress, setBotAddress] = useState<string>('')

  const [botLevel, setBotLevel] = useState<number>(1) // AGC: Store bot level for interactive battle

  const [isPaying, setIsPaying] = useState(false)

  const [isBattling, setIsBattling] = useState(false)

  const handleExitToHome = useCallback(() => {
    onNavClick('home')
  }, [onNavClick])

  const showStatusMessage = useCallback(

    (type: 'error' | 'success', message: string) => {

      setStatusBanner({ type, message })



      // AGC: Use modal for errors (works in Base App, Farcaster, and desktop)
      if (type === 'error') {
        alert(message, { type: 'error' })
      }

    },

    [isFarcasterContext]

  )



  const resolveSigner = useCallback(async () => {

    return getSafeSigner({

      connector,

      isFarcasterContext,

      expectedAddress: playerAddress as `0x${string}`

    })

  }, [connector, isFarcasterContext, playerAddress])

  const [battleMode, setBattleMode] = useState<'onchain' | 'hybrid' | null>(null) // AGC: Battle mode choice
  const handleModeSelectionRef = useRef<((mode: 'onchain' | 'hybrid', levelOverride?: number, buffsOverride?: number[]) => Promise<void>) | null>(null) // AGC: Ref for event listener to access latest function with optional level and buffs

  const [showResultModal, setShowResultModal] = useState(false)

  const [battleResult, setBattleResult] = useState<{ won: boolean, isDraw: boolean }>({ won: false, isDraw: false })

  const startBattleButtonRef = useRef<HTMLButtonElement | null>(null) // AGC: Scroll target for the primary CTA on mobile

  const [highlightStartButton, setHighlightStartButton] = useState(false) // AGC: Flash ring for the start battle button

  const hasDifficultyInteractionRef = useRef(false) // AGC: Flag to avoid auto-scroll before user interacts

  const hasBuffSelectionInteractionRef = useRef(false) // AGC: Track NFT selection interactions



  // AGC: Helper to bring key CTAs into view on mobile and highlight them

  const focusMobileButton = (
    buttonRef: React.RefObject<HTMLButtonElement | null>,
    setHighlight: (value: boolean) => void,
  ) => {

    const button = buttonRef.current

    if (!button) return



    if (typeof window !== 'undefined' && window.innerWidth <= 768) {

      button.scrollIntoView({ behavior: 'smooth', block: 'center' })

    }



    try {

      button.focus({ preventScroll: true })

    } catch (error) {

      button.focus()

    }

    setHighlight(true)

  }



  // AGC: Scroll to top when component mounts to prevent scroll position issues
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])


  // AGC: Remove the highlight after a short delay to avoid constant animation

  useEffect(() => {

    if (!highlightStartButton) return

    const timeoutId = setTimeout(() => setHighlightStartButton(false), 3000)

    return () => clearTimeout(timeoutId)

  }, [highlightStartButton])

  

  const wait = useCallback((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)), [])



  const handleDifficultySelect = (value: Difficulty) => {

    unlockAudioPlayback()

    setDifficulty(value)

    hasDifficultyInteractionRef.current = true

    focusMobileButton(startBattleButtonRef, setHighlightStartButton)

  }



  const handleBuffSelection = (combinedItems: number[]) => {
    unlockAudioPlayback()

    // AGC: Separate buffs (0-3) from potions (10000+) in combined array
    const buffs: number[] = []
    const potions: number[] = []
    
    for (const item of combinedItems) {
      if (item >= 10000) {
        // AGC: Potion (offset by 10000)
        potions.push(item - 10000)
      } else {
        // AGC: Buff (0-3)
        buffs.push(item)
      }
    }
    
    setSelectedBuffs(buffs)
    
    // AGC: Set potion (take first if multiple selected, since PvE only supports 1 potion)
    if (potions.length > 0) {
      setSelectedPotion(potions[0])
    } else {
      setSelectedPotion(null)
    }

    hasBuffSelectionInteractionRef.current = true

    focusMobileButton(startBattleButtonRef, setHighlightStartButton)

  }



  const syncOnChainBattle = useCallback(

    async (id: string): Promise<boolean> => {

      const maxAttempts = 5

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {

        try {

          const response = await fetch(buildBackendUrl('api/pve/battle/onchain-sync'), {

            method: 'POST',

            headers: {

              'Content-Type': 'application/json'

            },

            body: JSON.stringify({ battleId: id })

          })



          if (response.ok) {

            console.log('[OK] On-chain battle synced with backend')

            return true

          }



          if (response.status === 202) {

            console.log(`[WAIT] Battle ${id.slice(0, 10)} not ready on-chain (attempt ${attempt}/${maxAttempts})`)

            await wait(1000 * attempt)

            continue

          }



          const payload = await response.json().catch(() => ({}))

          console.warn('[WARN] Unexpected sync response:', response.status, payload)

          return false

        } catch (error) {

          const message = (error as Error).message

          console.warn(`[ERROR] Sync attempt ${attempt} failed: ${message}`)

          await wait(1000 * attempt)

        }

      }



      console.error('[ERROR] Failed to sync on-chain battle after maximum retries')

      return false

    },

    [wait]

  )



  // AGC: Resolve backend base URL dynamically (prod vs local dev)

  

  // AGC: OGB balance is now fetched via usePlayerStatus hook (same as TopNav)
  // AGC: PlayerProgressionPanel now handles all player stats display internally
  const ogbBalanceNumeric = ogbBalance // AGC: Still needed for payment checks
  const playerLevel = playerStats?.level ?? 1 // AGC: Fallback level for PlayerProgressionPanel


  /**
   * AGC: Step 0 - Show mode selection modal FIRST
   */

  const handleShowModeSelection = () => {

    unlockAudioPlayback()

    console.log(` Opening mode selection modal...`)

    // AGC: Call parent callback to show modal in MainMenu
    // AGC: CRITICAL - Combine selectedBuffs (0-3) with selectedPotion (10000+offset)
    // AGC: so potions are passed through the mode selection flow
    if (onShowModeSelection) {
      const combinedBuffs = [...selectedBuffs]
      if (selectedPotion !== null) {
        combinedBuffs.push(10000 + selectedPotion) // AGC: Add potion with offset
        console.log(`   Including potion ${selectedPotion} in mode selection (as ${10000 + selectedPotion})`)
      }
      console.log(`   Selected buffs+potions for mode selection: [${combinedBuffs.join(', ')}]`)
      
      onShowModeSelection({
        battleId: battleId || '',
        difficulty,
        selectedBuffs: combinedBuffs, // AGC: Now includes potions with 10000+ offset
        playerLevel: playerStats?.level || playerLevel
      })
    }

  }



  /**
   * AGC: Step 1 - Player approves and pays 50 OGB + NFTs
   * AGC: CRITICAL - Pass buffs array directly to avoid stale state
   */

  const handlePlayerPayment = async (battleId: string, selectedMode: 'onchain' | 'hybrid', buffsToUse: number[]) => {

    try {

      console.log(` Player paying for battle (${selectedMode} mode)...`)
      console.log(` Using ${buffsToUse.length} NFT buff(s):`, buffsToUse)

      

      setStatusBanner(null)

      const signer = await resolveSigner()

      const connectedAddress = await signer.getAddress()

      

      // AGC: Get correct PvE Manager address based on battle mode (use parameter, NOT state!)

      let PVE_MANAGER_ADDRESS

      if (selectedMode === 'onchain') {

        PVE_MANAGER_ADDRESS = import.meta.env.VITE_PVE_MANAGER_V4_ADDRESS

        console.log(` Using V4 contract (On-Chain Mode): ${PVE_MANAGER_ADDRESS}`)

      } else {

        // AGC: Prefer V7 (supports potion escrow), fall back to V6, then default, then legacy V5

        PVE_MANAGER_ADDRESS =

          import.meta.env.VITE_PVE_MANAGER_V6_ADDRESS || // AGC: V6 variable now points to V7 address

          import.meta.env.VITE_PVE_MANAGER_ADDRESS ||

          import.meta.env.VITE_PVE_MANAGER_V5_ADDRESS

        console.log(` Using V7 contract (Hybrid Mode with Potion Escrow): ${PVE_MANAGER_ADDRESS}`)

      }

      

      // AGC: Use NEW unified OGB token from environment

      const OGB_TOKEN_ADDRESS = import.meta.env.VITE_OGB_TOKEN_ADDRESS

      const BUFF1155_ADDRESS = import.meta.env.VITE_BUFF1155_ADDRESS

      

      // AGC: Debug - Log contract addresses

      console.log(` OGB_TOKEN_ADDRESS: ${OGB_TOKEN_ADDRESS}`)

      console.log(` BUFF1155_ADDRESS: ${BUFF1155_ADDRESS}`)

      console.log(` Battle Mode: ${selectedMode}`)



      // AGC: 1. Approve OGB (check allowance first, only approve if needed)

      console.log(`   Checking OGB allowance...`)

      const walletProvider = typeof window !== 'undefined' ? (window.ethereum as any) : undefined

      console.log(`   Wallet: ${walletProvider?.isMetaMask ? 'MetaMask' : walletProvider?.isCoinbaseWallet ? 'Coinbase Wallet' : connector?.id || 'Unknown'}`)

      

      // AGC: Add allowance check to avoid repeated approvals
      const ogbABI = [
        'function approve(address spender, uint256 amount) external returns (bool)',
        'function allowance(address owner, address spender) external view returns (uint256)'
      ]

      const ogbToken = new ethers.Contract(OGB_TOKEN_ADDRESS, ogbABI, signer)

      // AGC: Check current allowance - battle costs 50 OGB
      const requiredAllowance = ethers.parseEther('50')
      const currentAllowance = await ogbToken.allowance(connectedAddress, PVE_MANAGER_ADDRESS)
      console.log(`   Current allowance: ${ethers.formatEther(currentAllowance)} OGB`)

      // AGC: Log to backend debug system
      const logDebug = async (level: string, message: string, data?: any) => {
        try {
          await fetch(buildBackendUrl('api/debug/log'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              level,
              message,
              data: data || {},
              timestamp: Date.now(),
              source: 'PVEBattle'
            })
          }).catch(() => {}) // AGC: Silently fail if backend unavailable
        } catch {}
      }

      // AGC: Only approve if current allowance is less than required
      if (currentAllowance >= requiredAllowance) {
        console.log(`  [OK] OGB already approved (${ethers.formatEther(currentAllowance)} OGB >= 50 OGB required)`)
        await logDebug('info', 'OGB already approved - skipping approval', {
          currentAllowance: ethers.formatEther(currentAllowance),
          required: '50',
          wallet: connectedAddress
        })
      } else {
        console.log(`   Approving OGB (unlimited for better UX)...`)
        console.log(`  [WAIT] Check your wallet extension if popup doesn't appear!`)

        // AGC: Add timeout warning
        const timeoutWarning = setTimeout(() => {
          console.warn('[WARN] Wallet approval taking longer than expected...')
          console.warn(' Check your wallet extension icon in the browser toolbar!')
        }, 5000)

      try {

        await logDebug('info', 'Starting OGB approval', {

          contract: PVE_MANAGER_ADDRESS,

          amount: 'MaxUint256',

          wallet: connectedAddress,

          mode: selectedMode

        })

        
        // AGC: Approve MaxUint256 for unlimited future battles (one-time approval)
        const approveTx = await ogbToken.approve(PVE_MANAGER_ADDRESS, ethers.MaxUint256)

        clearTimeout(timeoutWarning)

        

        await logDebug('info', 'OGB approval transaction sent', {

          txHash: approveTx.hash,

          contract: PVE_MANAGER_ADDRESS

        })

        

        // AGC: Wait for transaction with timeout (Base App compatibility)
        try {
          // AGC: Use Promise.race to add timeout (120 seconds for Base App)
          await Promise.race([
            approveTx.wait(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Transaction confirmation timeout after 120 seconds')), 120000)
            )
          ])
          console.log(`  [OK] OGB approved`)
        } catch (waitError: any) {
          // AGC: If timeout, check if transaction was actually sent
          if (waitError.message?.includes('timeout')) {
            console.warn(`  ⚠️  Transaction confirmation timeout (TX sent: ${approveTx.hash})`)
            console.warn(`  ⚠️  Transaction may still be pending - checking status...`)
            
            // AGC: Try to check transaction status using public client (Base App compatible)
            try {
              const { usePublicClient } = await import('wagmi')
              // AGC: For now, just log and continue - transaction is already sent
              // AGC: The payment flow will continue and backend will handle verification
              console.log(`  ⚠️  Continuing with payment flow - transaction ${approveTx.hash} is pending`)
            } catch (checkError) {
              // AGC: Ignore check errors - transaction is already sent
            }
            
            // AGC: Don't throw error - transaction is sent, just not confirmed yet
            // AGC: Backend will verify when player pays
            console.log(`  [OK] OGB approval transaction sent (confirmation pending)`)
          } else {
            throw waitError
          }
        }

        

        await logDebug('info', 'OGB approval confirmed', {

          txHash: approveTx.hash

        })

        

        // AGC: Wait for blockchain state to propagate

        console.log(`  [WAIT] Waiting for approval to propagate...`)

        await new Promise(resolve => setTimeout(resolve, 2000))

      } catch (txError: any) {

        clearTimeout(timeoutWarning)

        

        // AGC: Log error to backend

        try {

          await fetch(buildBackendUrl('api/debug/log'), {

            method: 'POST',

            headers: { 'Content-Type': 'application/json' },

            body: JSON.stringify({

              level: 'error',

              message: 'OGB approval failed',

              data: {

                error: txError.message,

                code: txError.code,

                contract: PVE_MANAGER_ADDRESS,

                wallet: connectedAddress,

                mode: selectedMode

              },

              timestamp: Date.now(),

              source: 'PVEBattle'

            })

          }).catch(() => {})

        } catch {}

        

        // AGC: Better error messages

        if (txError.message?.includes('User rejected') || txError.message?.includes('User denied') || txError.code === 4001) {

          throw new Error('Transaction rejected by user')

        } else if (txError.message?.includes('insufficient funds')) {

          throw new Error('Insufficient OGB balance (need 50 OGB)')

        }

        throw txError

      }
      } // AGC: End of else block (approval needed)

      // AGC: 2. If player has NFTs, approve NFTs (buffs and/or potions)

      if (buffsToUse.length > 0) {

        console.log(`   Checking NFT approval for ${buffsToUse.length} NFT(s)...`)

        const nftABI = [

          'function setApprovalForAll(address operator, bool approved) external',

          'function isApprovedForAll(address account, address operator) external view returns (bool)',

          'function balanceOf(address account, uint256 id) external view returns (uint256)'

        ]

        // AGC: Separate buffs (0-3) from potions (10000+)
        const buffIds: number[] = []
        const potionIds: number[] = []
        
        for (const tokenId of buffsToUse) {
          if (tokenId >= 10000) {
            // AGC: Potion (offset by 10000)
            potionIds.push(tokenId - 10000)
          } else {
            // AGC: Buff (0-3)
            buffIds.push(tokenId)
          }
        }

        console.log(`   Buffs: ${buffIds.length}, Potions: ${potionIds.length}`)

        // AGC: Verify and approve buffs from Buff1155 contract
        if (buffIds.length > 0) {
          const buffContract = new ethers.Contract(BUFF1155_ADDRESS, nftABI, signer)
          
          console.log(`   Verifying buff ownership...`)
          for (const tokenId of buffIds) {
            const balance = await buffContract.balanceOf(connectedAddress, tokenId)
            console.log(`    Buff ID ${tokenId}: Balance = ${balance}`)
            if (balance === 0n) {
              throw new Error(`You don't own any buff NFTs of type ${tokenId}. Please refresh and try again.`)
            }
          }
          console.log(`  [OK] Player owns all selected buffs`)

          // AGC: Check if buffs already approved
          const isBuffsApproved = await buffContract.isApprovedForAll(connectedAddress, PVE_MANAGER_ADDRESS)
          console.log(`   Buff approval status: ${isBuffsApproved}`)
          
          if (!isBuffsApproved) {
            console.log(`   Approving buffs...`)
            const approveBuffsTx = await buffContract.setApprovalForAll(PVE_MANAGER_ADDRESS, true)
            try {
              await Promise.race([
                approveBuffsTx.wait(),
                new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
                )
              ])
              console.log(`  [OK] Buffs approved`)
            } catch (waitError: any) {
              if (waitError.message?.includes('timeout')) {
                console.warn(`  ⚠️  Buff approval timeout (TX sent: ${approveBuffsTx.hash}) - continuing...`)
              } else {
                throw waitError
              }
            }
          }
        }

        // AGC: Verify and approve potions from ClassicPotion1155 contract
        if (potionIds.length > 0) {
          const CLASSIC_POTION1155_ADDRESS = import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS
          if (!CLASSIC_POTION1155_ADDRESS) {
            throw new Error('Classic Potion contract address not configured')
          }
          
          const potionContract = new ethers.Contract(CLASSIC_POTION1155_ADDRESS, nftABI, signer)
          
          console.log(`   Verifying potion ownership...`)
          for (const tokenId of potionIds) {
            const balance = await potionContract.balanceOf(connectedAddress, tokenId)
            console.log(`    Potion ID ${tokenId}: Balance = ${balance}`)
            if (balance === 0n) {
              throw new Error(`You don't own any potion NFTs of type ${tokenId}. Please refresh and try again.`)
            }
          }
          console.log(`  [OK] Player owns all selected potions`)

          // AGC: Check if potions already approved
          const isPotionsApproved = await potionContract.isApprovedForAll(connectedAddress, PVE_MANAGER_ADDRESS)
          console.log(`   Potion approval status: ${isPotionsApproved}`)
          
          if (!isPotionsApproved) {
            console.log(`   Approving potions...`)
            const approvePotionsTx = await potionContract.setApprovalForAll(PVE_MANAGER_ADDRESS, true)
            try {
              await Promise.race([
                approvePotionsTx.wait(),
                new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
                )
              ])
              console.log(`  [OK] Potions approved`)
            } catch (waitError: any) {
              if (waitError.message?.includes('timeout')) {
                console.warn(`  ⚠️  Potion approval timeout (TX sent: ${approvePotionsTx.hash}) - continuing...`)
              } else {
                throw waitError
              }
            }
          }
        }
      }

      

      // AGC: 3. Pay battle stake (with or without NFTs)

      const pveManagerABI = [

        'function payBattleStake(bytes32 battleId, address ogbTokenAddress) external',

        'function payBattleStakeWithBuffs(bytes32 battleId, address ogbTokenAddress, address buff1155Address, uint256[] calldata buffIds) external',

        'function payBattleStakeWithNFTs(bytes32 battleId, address ogbTokenAddress, address buff1155Address, address potion1155Address, uint256[] calldata buffIds, uint256[] calldata potionIds) external',

        'function battles(bytes32) public view returns (bytes32, address, address, uint256, bool, bool, bool, address, uint256, uint256)',

        'function trustedBuff1155Contracts(address) public view returns (bool)',

        'function trustedPotion1155Contracts(address) public view returns (bool)'

      ]

      const pveManager = new ethers.Contract(PVE_MANAGER_ADDRESS, pveManagerABI, signer)

      

      // AGC: Check battle state before paying

      console.log(`   Checking battle state...`)

      const battleState = await pveManager.battles(battleId)

      

      if (battleState[4]) { // playerPaid

        throw new Error('Player has already paid for this battle.')

      }

      

      console.log(`  [OK] Battle is valid and ready for payment`)

      

      // AGC: Player pays using payBattleStake() or payBattleStakeWithNFTs()
      // AGC: Bot pays using payForBattle() (handled by backend)

      // AGC: Separate buffs and potions from buffsToUse array
      const buffIdsForPayment: number[] = []
      const potionIdsForPayment: number[] = []
      
      for (const tokenId of buffsToUse) {
        if (tokenId >= 10000) {
          // AGC: Potion (offset by 10000)
          potionIdsForPayment.push(tokenId - 10000)
        } else {
          // AGC: Buff (0-3)
          buffIdsForPayment.push(tokenId)
        }
      }
      
      // AGC: Log selected NFTs for debugging
      console.log(`   Selected buffs: [${buffIdsForPayment.join(', ')}]`)
      console.log(`   Selected potions: [${potionIdsForPayment.join(', ')}]`)
      
      // AGC: V7 Contract - Supports both buff and potion escrow
      const CLASSIC_POTION1155_ADDRESS = import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS
      
      // AGC: Check if we have NFTs to escrow
      const hasBuffs = buffIdsForPayment.length > 0
      const hasPotions = potionIdsForPayment.length > 0
      
      if (hasBuffs || hasPotions) {
        // AGC: Verify contracts are trusted
        if (hasBuffs) {
          console.log(`   Verifying Buff1155 contract is trusted...`)
          const isBuffTrusted = await pveManager.trustedBuff1155Contracts(BUFF1155_ADDRESS)
          if (!isBuffTrusted) {
            throw new Error('Buff1155 contract is not trusted by PvEManagerV7')
          }
          console.log(`  [OK] Buff1155 contract is trusted`)
        }
        
        if (hasPotions) {
          if (!CLASSIC_POTION1155_ADDRESS) {
            throw new Error('ClassicPotion1155 address not configured')
          }
          console.log(`   Verifying ClassicPotion1155 contract is trusted...`)
          const isPotionTrusted = await pveManager.trustedPotion1155Contracts(CLASSIC_POTION1155_ADDRESS)
          if (!isPotionTrusted) {
            throw new Error('ClassicPotion1155 contract is not trusted by PvEManagerV7')
          }
          console.log(`  [OK] ClassicPotion1155 contract is trusted`)
        }
        
        // AGC: Use V7's payBattleStakeWithNFTs for both buffs and potions
        console.log(`   Paying 50 OGB + ${buffIdsForPayment.length} Buff NFT(s) + ${potionIdsForPayment.length} Potion NFT(s)...`)
        const payTx = await pveManager.payBattleStakeWithNFTs(
          battleId,
          OGB_TOKEN_ADDRESS,
          BUFF1155_ADDRESS,
          CLASSIC_POTION1155_ADDRESS,
          buffIdsForPayment,
          potionIdsForPayment
        )
        try {
          await Promise.race([
            payTx.wait(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
            )
          ])
        } catch (waitError: any) {
          if (waitError.message?.includes('timeout')) {
            console.warn(`  ⚠️  Payment TX timeout (TX sent: ${payTx.hash}) - continuing...`)
          } else {
            throw waitError
          }
        }
        console.log(`[OK] NFTs escrowed! TX: ${payTx.hash}`)
      } else {
        // AGC: No NFTs - just pay OGB
        console.log(`   Paying 50 OGB...`)
        const payTx = await pveManager.payBattleStake(battleId, OGB_TOKEN_ADDRESS)
        try {
          await Promise.race([
            payTx.wait(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Transaction confirmation timeout')), 120000)
            )
          ])
        } catch (waitError: any) {
          if (waitError.message?.includes('timeout')) {
            console.warn(`  ⚠️  Payment TX timeout (TX sent: ${payTx.hash}) - continuing...`)
          } else {
            throw waitError
          }
        }
        console.log(`[OK] Payment confirmed!`)
      }

      

      console.log(`[OK] Payment confirmed!`)

      

      // AGC: Trigger bot payment immediately after player pays

      console.log(` Triggering bot payment...`)

      try {

        const botPayResponse = await fetch(buildBackendUrl('api/pve/battle/bot-pay'), {

          method: 'POST',

          headers: { 'Content-Type': 'application/json' },

          body: JSON.stringify({ battleId })

        })

        

        if (!botPayResponse.ok) {

          const errorData = await botPayResponse.json()

          throw new Error(errorData.message || 'Bot payment failed')

        }

        

        const botPayData = await botPayResponse.json()

        console.log(`[OK] Bot payment successful: ${botPayData.txHash}`)

      } catch (botPayError: any) {

        console.error('[WARN] Bot payment error:', botPayError.message)

        // AGC: Don't block battle - bot can pay during resolve if needed

      }

      

      // AGC: CRITICAL - Wait for contract state to propagate before continuing

      console.log(`[WAIT] Waiting for contract state to propagate...`)

      let stateConfirmed = false

      let attempts = 0

      const maxAttempts = 10 // AGC: Allow up to ~10 seconds for Base Sepolia finality

      

      while (!stateConfirmed && attempts < maxAttempts) {

        attempts++

        console.log(`   Attempt ${attempts}/${maxAttempts}: Checking payment status...`)

        

        try {

          // AGC: Read battle state from contract

          const battleABI = ['function battles(bytes32) view returns (bytes32 battleId, address player, address bot, uint256 stake, bool playerPaid, bool botPaid, bool resolved, address winner, uint8 playerHP, uint8 botHP, uint8 playerLevel, uint8 botLevel, uint8 currentTurn, uint256 createdAt, uint256 resolvedAt, bool refunded)']

          const provider = new ethers.JsonRpcProvider(ACTIVE_RPC_URL)

          const contract = new ethers.Contract(PVE_MANAGER_ADDRESS, battleABI, provider)

          const battle = await contract.battles(battleId)

          

          console.log(`    Player Paid: ${battle.playerPaid}, Bot Paid: ${battle.botPaid}`)

          

          if (battle.playerPaid && battle.botPaid) {

            console.log(`  [OK] Both payments confirmed on contract!`)

            stateConfirmed = true

            break

          }

          

          // AGC: Wait 1 second before next check (optimized for speed)

          if (attempts < maxAttempts) {

            console.log(`  [WAIT] Waiting 1 second before retry...`)

            await new Promise(resolve => setTimeout(resolve, 1000))

          }

        } catch (checkError: any) {

          // AGC: Ignore Coinbase Wallet proxy errors (harmless)

          if (checkError.message?.includes('chain-proxy.wallet.coinbase.com') || 

              checkError.message?.includes('CORS') ||

              checkError.message?.includes('502')) {

            console.log(`  [WARN] Coinbase Wallet proxy error (ignoring)...`)

          } else {

            console.error(`  [WARN] State check error:`, checkError.message)

          }

          if (attempts < maxAttempts) {

            await new Promise(resolve => setTimeout(resolve, 1000))

          }

        }

      }

      

      if (!stateConfirmed) {

        throw new Error('Contract state has not updated yet. Please wait a few seconds and try again.')

      }

      

      setIsPaying(false)

      

      // AGC: Payment complete - start battle based on selected mode (use parameter!)

      console.log(`[OK] Payments confirmed! Starting ${selectedMode} battle...`)

      

      if (selectedMode === 'onchain') {

        // AGC: Start on-chain interactive battle (turn-by-turn)

        setIsBattling(true)

        setStatusBanner({

          type: 'success',

          message: 'Processing battle on Base… result will appear once on-chain resolution finalizes.'

        })

      } else {

        // AGC: Start hybrid battle (WebSocket-based)

        handleHybridBattle()

      }

      

    } catch (error: any) {

      console.error('[ERROR] Payment error:', error)

      showStatusMessage('error', error?.message ? `Payment failed: ${error.message}` : 'Payment failed. Please try again.')

      setIsPaying(false)

    }

  }



  /**
   * AGC: Handle mode selection (on-chain or hybrid)
   */

  const handleModeSelection = async (mode: 'onchain' | 'hybrid', levelOverride?: number, buffsOverride?: number[]) => {

    unlockAudioPlayback()

    console.log(` Player chose ${mode} mode`)

    setBattleMode(mode)

    

    // AGC: Start the battle with the selected mode

    try {

      setIsPaying(true)

      

      // AGC: CRITICAL FIX - Use buffsOverride if provided (from event), otherwise fall back to state
      // AGC: Note - buffsOverride should already be separated (only buffs 0-3, no potions)
      const effectiveBuffs = buffsOverride !== undefined ? buffsOverride : selectedBuffs
      console.log(`[BUFFS] Using buffs: ${JSON.stringify(effectiveBuffs)} (source: ${buffsOverride !== undefined ? 'event' : 'state'})`)
      
      // AGC: Ensure effectiveBuffs only contains actual buffs (0-3), not potions
      const actualBuffs = effectiveBuffs.filter(buff => {
        const numericId = Number(buff)
        return Number.isFinite(numericId) && numericId >= 0 && numericId <= 3
      })
      
      // AGC: Silently filter out potion IDs (4+) from buff array - this is expected behavior
      if (actualBuffs.length !== effectiveBuffs.length) {
        // AGC: Only log in debug mode - potions are expected to be filtered here
        if (import.meta.env.DEV) {
          console.debug(`[DEBUG] Filtered out ${effectiveBuffs.length - actualBuffs.length} potion ID(s) from effectiveBuffs (expected behavior)`)
        }
      }

      console.log(` Starting PvE battle (${difficulty}, ${mode} mode)...`)

      

      // AGC: Use levelOverride if provided (from event), otherwise wait for playerStats

      let actualPlayerLevel: number

      

      if (levelOverride) {

        actualPlayerLevel = levelOverride

        console.log(`[OK] Using player level from event: ${actualPlayerLevel}`)

      } else if (!playerStats || !playerStats.level) {

        console.warn('[WARN] Player stats not loaded yet, waiting...')

        // AGC: Wait a bit for playerStats to load (max 3 seconds)

        let attempts = 0

        while ((!playerStats || !playerStats.level) && attempts < 30) {

          await new Promise(resolve => setTimeout(resolve, 100))

          attempts++

        }

        if (!playerStats || !playerStats.level) {

          throw new Error('Failed to load player level. Please refresh and try again.')

        }

        actualPlayerLevel = playerStats.level

        console.log(`[OK] Using player level from playerStats: ${actualPlayerLevel}`)

      } else {

        actualPlayerLevel = playerStats.level

        console.log(`[OK] Using player level from playerStats: ${actualPlayerLevel}`)

      }

      

      // AGC: Call backend to create battle and bot pays
      const backendUrl = buildBackendUrl('api/pve/battle/start')
      console.log(`[DEBUG] Calling backend: ${backendUrl}`)
      console.log(`[DEBUG] Request body:`, {
        playerAddress,
        playerLevel: actualPlayerLevel,
        difficulty,
        nftIds: actualBuffs, // AGC: Only send actual buffs (0-3), not potions
        mode
      })

      // AGC: CRITICAL - Simple fetch without AbortController for Base App compatibility
      // AGC: AbortController and setTimeout don't work reliably in Base App environment
      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerAddress,
          playerLevel: actualPlayerLevel,
          difficulty,
          nftIds: actualBuffs, // AGC: Only send actual buffs (0-3), not potions
          mode
        })
      })

      console.log(`[DEBUG] Backend response status: ${response.status} ${response.statusText}`)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error(`[ERROR] Backend returned error: ${response.status}`, errorText)
        throw new Error(`Backend error (${response.status}): ${errorText || 'Unknown error'}`)
      }

      const data = await response.json()
      console.log(`[DEBUG] Backend response data:`, data)

      

      if (!data.success) {

        throw new Error(data.message || 'Failed to start battle')

      }

      

      console.log(`[OK] Battle created: ${data.battleId}`)

      console.log(` Bot address: ${data.botAddress}`)

      console.log(` Bot level: ${data.botLevel}`)

      console.log(` Mode: ${mode}`)

      

      setBattleId(data.battleId)

      setBotAddress(data.botAddress)

      setBotLevel(data.botLevel || 1)

      

      // AGC: CRITICAL FIX - Now player must approve and pay (use effectiveBuffs!)

      await handlePlayerPayment(data.battleId, mode, effectiveBuffs)

      showStatusMessage('success', 'Payment confirmed! Waiting for bot to join…')

      

    } catch (error: any) {

      console.error('[ERROR] Error starting battle:', error)

      showStatusMessage('error', error.message || 'Failed to start battle')

      setIsPaying(false)

    }

  }



  /**
   * AGC: Handle hybrid battle (WebSocket-based, real-time like P2P)
   */

  const handleHybridBattle = () => {

    console.log(` Starting hybrid battle (WebSocket mode)...`)

    setIsBattling(true)

    setStatusBanner({

      type: 'success',

      message: 'Processing battle on Base… result will appear once the hybrid engine completes.'

    })

    // AGC: The HybridPvEBattle component will handle the rest via WebSocket

  }

  // AGC: Store handleModeSelection in ref so event listener can access latest version
  useEffect(() => {
    handleModeSelectionRef.current = handleModeSelection
  }, [handleModeSelection])

  // AGC: Listen for mode selection event from PVESelection page
  useEffect(() => {
    const handleModeSelectedEvent = (event: CustomEvent) => {
      const { mode, data } = event.detail
      console.log(`[PVEBattle] Mode selected via event: ${mode}`, data)
      
      // AGC: Update local state with data from event if provided
      if (data) {
        if (data.difficulty) setDifficulty(data.difficulty as Difficulty)
        if (data.selectedBuffs) setSelectedBuffs(data.selectedBuffs)
        if (data.battleId) setBattleId(data.battleId)
      }
      
      // AGC: CRITICAL FIX - Pass buffs from event directly to avoid stale state
      // AGC: DON'T separate potions here - pass combined array to handleModeSelection
      // AGC: The payment function (handlePlayerPayment) will separate them correctly
      if (handleModeSelectionRef.current) {
        const levelOverride = data?.playerLevel || undefined
        const buffsOverride = data?.selectedBuffs || undefined
        
        // AGC: Log what we're passing for debugging
        if (buffsOverride && Array.isArray(buffsOverride)) {
          const hasBuffs = buffsOverride.some(id => id < 10000)
          const hasPotions = buffsOverride.some(id => id >= 10000)
          console.log(`   Passing to battle: ${buffsOverride.length} items (buffs: ${hasBuffs}, potions: ${hasPotions})`)
          console.log(`   Combined IDs: [${buffsOverride.join(', ')}]`)
          
          // AGC: Also set selectedPotion state for UI display (but don't strip from override)
          const potionId = buffsOverride.find(id => id >= 10000)
          if (potionId !== undefined) {
            setSelectedPotion(potionId - 10000)
            console.log(`   Setting selectedPotion state: ${potionId - 10000}`)
          }
        }
        
        handleModeSelectionRef.current(mode, levelOverride, buffsOverride).catch((error) => {
          console.error('[PVEBattle] Error handling mode selection:', error)
        })
      } else {
        console.error('[PVEBattle] handleModeSelection not available!')
      }
    }
    
    window.addEventListener('quickbattle:mode-selected', handleModeSelectedEvent as EventListener)
    return () => {
      window.removeEventListener('quickbattle:mode-selected', handleModeSelectedEvent as EventListener)
    }
  }, []) // AGC: Empty deps - using ref pattern to access latest handleModeSelection



  /**
   * AGC: Handle battle completion from interactive component
   */

  const handleBattleComplete = async (won: boolean, turns: number, isDraw: boolean = false, resolutionTxHash: string | null = null) => {

    try {

      console.log(` Battle ended - Won: ${won}, Turns: ${turns}, Draw: ${isDraw}`)

      console.log(`  Resolution TX: ${resolutionTxHash || 'Not available'}`)

      console.log(`  Mode: ${battleMode}`)

      

      // AGC: Add battle record

      const outcome: 'WIN' | 'LOSS' | 'DRAW' = isDraw ? 'DRAW' : won ? 'WIN' : 'LOSS'

      const tokensResult = isDraw ? 25 : won ? 75 : 0

      const tokensLost = isDraw ? 25 : won ? 0 : 50

      

      // AGC: Determine battle type based on mode

      const battleType: 'PVE' | 'PVP' | undefined = 'PVE'

      const xpOutcome: 'WIN' | 'DRAW' | 'LOSE' = outcome === 'DRAW' ? 'DRAW' : won ? 'WIN' : 'LOSE'

      const experienceGained = calculateBattleXP(xpOutcome, botLevel, 'PVE')

      

      addBattleRecord({

        outcome,

        turns,

        isKnockout: won && turns <= 3, // AGC: KO if won in 3 or fewer turns (matches P2P logic)

        opponentLevel: botLevel,

        playerLevel: playerStats?.level || 1,

        experienceGained,

        tokensWon: tokensResult,

        tokensLost: tokensLost,

        opponentAddress: undefined, // AGC: undefined for bot opponent (PvE battles)

        battleType: battleType, // AGC: PVE_ONCHAIN for on-chain, PVE for hybrid

        battleId: battleId, // AGC: Add battleId for profile link

        resolutionTxHash: resolutionTxHash || undefined // AGC: FIX - Add tx hash for BaseScan link on profile

      })



      let syncSucceeded = true

      if (battleMode === 'onchain' && battleId) {

        syncSucceeded = await syncOnChainBattle(battleId)

        if (syncSucceeded) {

          window.dispatchEvent(new CustomEvent('origami:refresh-recent-battles'))

        }

      }

      

      // AGC: Show result modal

      setStatusBanner(null)

      setBattleResult({ won, isDraw })

      setShowResultModal(true)

      setIsBattling(false)

      

      // AGC: Call completion callback

      if (onBattleComplete) {

        onBattleComplete({

          playerAddress,

          won,

          turns,

          isDraw

        })

      }



      if (!syncSucceeded) {

        console.warn('[WARN] On-chain battle sync did not complete successfully; recent battles may refresh later.')

      }

      

    } catch (error: any) {

      console.error('[ERROR] Error completing battle:', error)

    }

  }



  // AGC: If on-chain battle is active, show interactive battle component

  if (isBattling && battleMode === 'onchain' && battleId && botAddress) {
    return (
      <div className="w-full">
        <InteractivePvEBattle
          battleId={battleId}
          playerAddress={playerAddress}
          botAddress={botAddress}
          botLevel={botLevel}
          selectedBuffs={selectedBuffs}
          selectedPotion={selectedPotion}
          onBattleEnd={handleBattleComplete}
          onBackToMenu={handleExitToHome}
        />
      </div>
    )
  }

  

  // AGC: If hybrid battle is running, show HybridPvEBattle component

  if (isBattling && battleMode === 'hybrid') {
    const hybridContent = !socket || !isConnected ? (
      <div className="flex min-h-[60vh] w-full items-center justify-center px-4">
          <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-3xl bg-slate-900/80 px-6 py-10 text-center text-white shadow-2xl">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
            <p className="text-lg font-semibold">Connecting to backend…</p>
            <p className="text-sm text-slate-300">Hybrid mode (real-time)</p>
          </div>
        </div>
    ) : (
      <HybridPvEBattle
        battleId={battleId}
        playerAddress={playerAddress}
        botLevel={botLevel}
        socket={socket}
        selectedPotion={selectedPotion}
        selectedBuffs={selectedBuffs}
        onBattleEnd={handleBattleComplete}
        onBackToMenu={handleExitToHome}
      />
    )

    return (
      <div className="w-full">{hybridContent}</div>
    )
  }

  

  // AGC: Top padding style matching Profile.tsx - 72px to clear TopNav (60px) + spacing
  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 72px)' }

  // AGC: Calculate XP progress for display (matching Profile.tsx)
  const xpCurrent = playerStats?.experience ?? 0
  const xpToNext = playerStats?.experienceToNext ?? 0
  const xpTarget = xpCurrent + Math.max(0, xpToNext)
  const xpProgress = xpTarget > 0 ? Math.min(1, xpCurrent / xpTarget) : 0
  const attack = playerStats?.attack ?? 0
  const defense = playerStats?.defense ?? 0
  const walletShort = `${playerAddress.slice(0, 4)}…${playerAddress.slice(-4)}`

  return (
    <main
    className="pb-[88px] px-3 text-white flex flex-col gap-1.5"
    style={sharedTopPaddingStyle}
  >
    {/* AGC: Hero Header (No Card) - Following Profile.tsx pattern exactly */}
    <section data-tour="battle-player-progression" className="pb-2 border-b border-white/10">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">PVE BATTLE</p>
          <h1 className="text-2xl font-bold leading-tight text-white">Lv {playerLevel}</h1>
          <p className="text-[11px] text-slate-400">{walletShort} • ATK {attack} • DEF {defense}</p>
        </div>
        <a 
          href={`https://sepolia.basescan.org/address/${playerAddress}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
        >
          BaseScan
        </a>
      </div>
      <div className="mt-3">
        <div className="flex items-center justify-between text-[10px] text-slate-400 mb-1">
          <span>XP Progress</span>
          <span>{xpCurrent.toLocaleString()} / {xpTarget.toLocaleString()}</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden shadow-inner shadow-black/30">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 transition-all duration-500"
            style={{ width: `${xpProgress * 100}%` }}
          />
        </div>
      </div>
    </section>

      {/* AGC: Status Banner - Simplified design (following improve_design.md) */}
      {statusBanner && (
        <div
          className={`border-b px-3 py-2 text-[11px] font-semibold leading-tight ${
            statusBanner.type === 'error'
              ? 'border-rose-500/40 text-rose-100'
              : 'border-emerald-400/40 text-emerald-100'
          }`}
        >
          {statusBanner.message}
        </div>
      )}

      {/* AGC: Divider before Difficulty Selector */}
      {!statusBanner && <div className="border-b border-white/10"></div>}

      {/* AGC: Difficulty Selector - Using reusable component */}
      <DifficultySelector
        selectedDifficulty={difficulty}
        onDifficultySelect={handleDifficultySelect}
        disabled={isPaying || isBattling}
        variant="pve"
        battleCosts={{
          entryFee: '50 OGB',
          buffNFTsBurned: selectedBuffs.length || 0,
          winnerReceives: '75 OGB (stake + bounty)',
          drawRefund: '25 OGB each',
          xpAwarded: 'on-chain immediately',
        }}
      />

      {/* AGC: Divider before Buff Selector */}
      <div className="border-b border-white/10"></div>

      {/* AGC: Buff Selector - Reusable component */}
      <BuffSelector
        playerAddress={playerAddress}
        onBuffsSelected={handleBuffSelection}
        maxBuffs={2}
        onNavigateToMarketplace={onNavigateToMarketplace}
        disabled={isPaying || isBattling}
        variant="compact"
      />

      <div className="mt-2">
        <button
          ref={startBattleButtonRef}
          data-tour="battle-start-button"
          onClick={handleShowModeSelection}
          disabled={isPaying || isBattling || ogbBalanceNumeric < 50}
          className={`w-full rounded-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-lg disabled:cursor-not-allowed text-sm uppercase tracking-wide ${
            highlightStartButton ? 'ring-2 ring-amber-400/70 ring-offset-2 ring-offset-slate-950' : ''
          }`}
        >
          {isPaying ? 'Processing…' : isBattling ? 'Battling…' : ogbBalanceNumeric < 50 ? '⚠️ Insufficient OGB Balance' : 'Start Battle'}
        </button>
      </div>

      {ogbBalanceNumeric < 50 && (
        <div className="text-center text-[11px] font-semibold text-amber-300 pb-2 border-b border-white/10">
          ⚠️ Need at least 50 OGB to battle.
        </div>
      )}

      {/* AGC: Battle Result Modal */}

      <BattleResultModal

        isOpen={showResultModal}

        won={battleResult.won}

        isDraw={battleResult.isDraw}

        onClose={() => {

          setShowResultModal(false)

          setBattleId('')

          setBotAddress('')

          setSelectedBuffs([])

          hasBuffSelectionInteractionRef.current = false

          hasDifficultyInteractionRef.current = false

        }}

        autoCloseDelay={5000}

      />




      {/* AGC: Modal components */}
      {Modals}
    </main>
  )
}




